// A minimal, STORE-ONLY ZIP writer.
//
// WHY NOT A LIBRARY. Every file this ever packs is an already-compressed photo
// (JPEG, PNG, WebP, AVIF). DEFLATE buys 0-2% on those and costs CPU proportional
// to the whole input, which for a trip's photos is up to a gigabyte. So the only
// thing a zip library would provide that we would actually use is the container
// format -- four record layouts and a CRC-32 -- and its real selling point,
// streaming DEFLATE in a worker, is precisely the part we would be shipping and
// not using.
//
// fflate IS a devDependency, though, and zipStore.test.js unzips our own output
// with it and compares bytes. That buys the correctness confidence a library
// would, at zero cost in the production bundle.
//
// WHAT THIS DELIBERATELY DOESN'T DO:
//
//   - No ZIP64. Every size and offset here is 32-bit, so the archive cannot
//     exceed 4 GiB. `assertWithinZip32` is the guard, and photoZip.mjs's
//     MAX_ZIP_BYTES (1 GiB) keeps callers well clear of it. Implementing ZIP64
//     for an archive nobody can usefully download would be the wrong trade.
//   - No UTF-8 general-purpose flag. Names are sanitised to ASCII instead
//     (`zipSafeName`), because the flag is honoured inconsistently by desktop
//     unzippers and a mangled name is worse than a transliterated one.
//   - No directory entries. A path with a `/` in it is what makes a folder in
//     every unzipper; explicit directory records add nothing.

// The 32-bit ceiling every field in this format has.
export const ZIP32_LIMIT = 0xffffffff;

// ---- CRC-32 -----------------------------------------------------------------
// The standard IEEE 802.3 polynomial, reflected, exactly as the ZIP spec wants.
// Table built once on first use rather than at module load: a page that never
// zips anything shouldn't pay for it.
let crcTable = null;

function crc32Table() {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  crcTable = table;
  return crcTable;
}

// CRC-32 of a Uint8Array. Exported for the test's known-answer vector.
export function crc32(bytes) {
  const table = crc32Table();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// ---- Names ------------------------------------------------------------------

// One path component, or an `a/b` path, reduced to bytes every unzipper agrees
// about. Keeps `/` (it is what makes a folder) and drops anything that would
// make a hostile or unportable name: a leading slash, `..`, a drive letter,
// control characters, and the Windows-reserved punctuation.
//
// IT ALSO REDUCES TO ASCII, which is what lets `asciiBytes` below be three lines
// instead of a TextEncoder. `Mölle.jpg` becomes `Molle.jpg` via NFD plus a
// combining-mark strip, and anything with no ASCII spelling becomes `-`. The
// alternative -- UTF-8 names plus bit 11 of the general-purpose flags -- is
// honoured inconsistently enough by desktop unzippers that a transliterated name
// is the kinder outcome than a mojibake one.
export function zipSafeName(name) {
  const cleaned = String(name ?? '')
    .normalize('NFD')
    // Combining marks, i.e. the accents NFD just split off.
    .replace(/[\u0300-\u036f]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\:*?"<>|]/g, '-')
    // Everything still outside printable ASCII.
    .replace(/[^\u0020-\u007e/]/g, '-')
    .split('/')
    .map((part) => part.replace(/^\.+$/, '').trim())
    .filter((part) => part !== '')
    .join('/');
  return cleaned === '' ? 'file' : cleaned;
}

// `photo.jpg` -> `photo-2.jpg` on the second use, `photo-3.jpg` on the third.
// The suffix goes before the extension so the file still opens by double-click.
//
// `seen` is a Set the caller owns across a whole archive, because ZIP has no
// uniqueness rule of its own: duplicate names produce an archive whose entries
// silently overwrite each other on extraction.
export function uniqueZipName(name, seen) {
  if (!seen.has(name)) {
    seen.add(name);
    return name;
  }
  const dot = name.lastIndexOf('.');
  const slash = name.lastIndexOf('/');
  const hasExt = dot > slash + 1;
  const base = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : '';
  for (let n = 2; ; n += 1) {
    const candidate = `${base}-${n}${ext}`;
    if (!seen.has(candidate)) {
      seen.add(candidate);
      return candidate;
    }
  }
}

// ---- Record building --------------------------------------------------------

// zipSafeName has already reduced every name to printable ASCII, so this is the
// whole encoder this module needs -- and not reaching for TextEncoder keeps the
// module usable anywhere, jsdom included, with no polyfill.
function asciiBytes(text) {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) bytes[i] = text.charCodeAt(i) & 0x7f;
  return bytes;
}

function writer(size) {
  const bytes = new Uint8Array(size);
  const view = new DataView(bytes.buffer);
  let at = 0;
  return {
    bytes,
    u16(value) {
      view.setUint16(at, value, true);
      at += 2;
    },
    u32(value) {
      view.setUint32(at, value >>> 0, true);
      at += 4;
    },
    raw(chunk) {
      bytes.set(chunk, at);
      at += chunk.length;
    },
  };
}

// DOS date/time, which is what ZIP stores. Two-second resolution and a 1980
// epoch; a date before that clamps rather than wrapping into nonsense.
function dosDateTime(date) {
  const year = date.getFullYear();
  if (year < 1980) return { time: 0, date: 0x21 }; // 1980-01-01 00:00
  return {
    time:
      (Math.floor(date.getSeconds() / 2) & 0x1f) |
      ((date.getMinutes() & 0x3f) << 5) |
      ((date.getHours() & 0x1f) << 11),
    date: ((date.getDate() & 0x1f) | (((date.getMonth() + 1) & 0x0f) << 5) | ((year - 1980) << 9)) >>> 0,
  };
}

function assertWithinZip32(value, what) {
  if (value > ZIP32_LIMIT) {
    // Deliberately a throw and not a truncation: a 32-bit field silently
    // wrapping produces an archive that looks fine and extracts garbage.
    const err = new Error(`This download is too large to package (${what} exceeds 4 GB).`);
    err.name = 'ZipTooLargeError';
    throw err;
  }
}

/**
 * The local file header plus the bytes for one stored entry.
 *
 * Returns `{ header, record }` -- `header` is what goes into the archive ahead
 * of the file's bytes, and `record` is what the central directory needs later.
 * The caller keeps the bytes; this never holds onto them.
 */
export function zipEntry({ name, bytes, offset, date = new Date() }) {
  const safeName = zipSafeName(name);
  const nameBytes = asciiBytes(safeName);
  const crc = crc32(bytes);
  const { time, date: dosDate } = dosDateTime(date);

  assertWithinZip32(offset, 'the archive');
  assertWithinZip32(bytes.length, 'a photo');

  const out = writer(30 + nameBytes.length);
  out.u32(0x04034b50); // local file header signature
  out.u16(10); // version needed: 1.0, which is all "stored" requires
  out.u16(0); // general purpose flags: none (see the header note on UTF-8)
  out.u16(0); // method 0 = stored
  out.u16(time);
  out.u16(dosDate);
  out.u32(crc);
  out.u32(bytes.length); // compressed size == uncompressed, being stored
  out.u32(bytes.length);
  out.u16(nameBytes.length);
  out.u16(0); // extra field length
  out.raw(nameBytes);

  return {
    header: out.bytes,
    record: {
      name: safeName,
      nameBytes,
      crc,
      size: bytes.length,
      offset,
      time,
      dosDate,
    },
  };
}

/**
 * The central directory and end-of-central-directory record that close an
 * archive, given every entry's `record` and the offset just past the last
 * file's bytes.
 */
export function zipCentralDirectory(records, offset) {
  assertWithinZip32(offset, 'the archive');

  const size = records.reduce((total, record) => total + 46 + record.nameBytes.length, 0);
  const out = writer(size + 22);

  for (const record of records) {
    out.u32(0x02014b50); // central directory header signature
    out.u16(0x031e); // version made by: 3.0, Unix -- what unzippers expect
    out.u16(10); // version needed
    out.u16(0); // flags
    out.u16(0); // method 0 = stored
    out.u16(record.time);
    out.u16(record.dosDate);
    out.u32(record.crc);
    out.u32(record.size);
    out.u32(record.size);
    out.u16(record.nameBytes.length);
    out.u16(0); // extra
    out.u16(0); // comment
    out.u16(0); // disk number
    out.u16(0); // internal attributes
    // External attributes: 0644 in the high word, which is what keeps extracted
    // files readable rather than mode 000 on Unix.
    out.u32(0o644 << 16);
    out.u32(record.offset);
    out.raw(record.nameBytes);
  }

  out.u32(0x06054b50); // end of central directory signature
  out.u16(0); // this disk
  out.u16(0); // disk with the central directory
  out.u16(records.length);
  out.u16(records.length);
  out.u32(size);
  out.u32(offset);
  out.u16(0); // comment length

  return out.bytes;
}
